/**
 * MB Utilities
 * Common utility functions used across MB custom features
 */

/**
 * @param {Element} element
 * @returns {Element[]}
 */
function getAllSiblings(element) {
  // Get the parent element's children (returns an HTMLCollection of only elements)
  if (!element?.parentNode) return [];
  const children = element.parentNode.children;

  // Convert the HTMLCollection to an array and filter out the original element
  const siblings = Array.from(children).filter(sibling => sibling !== element);

  return siblings;
}


class MBTabs {
  constructor(container) {
    this.container = container;
    this.tabNav = container.querySelector('.tabs__nav');
    this.panels = container.querySelectorAll('[role="tabpanel"]');
    this.tabs = null;
    
    this.init();
  }
  
  init() {
    // Build tab buttons from panels
    this.buildTabButtons();
    
    // Get tab references
    this.tabs = this.tabNav.querySelectorAll('[role="tab"]');
    
    // Attach event listeners
    this.attachEventListeners();
    
    // Auto-activate tab from URL hash if present
    this.activateFromHash();
  }
  
  buildTabButtons() {
    this.panels.forEach((panel, index) => {
      const tabName = panel.dataset.tabName || ``;
      const tabId = panel.id.replace('panel-', 'tab-');
      const isFirst = index === 0;
      
      const button = document.createElement('button');
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', isFirst);
      button.setAttribute('aria-controls', panel.id);
      button.setAttribute('id', tabId);
      button.className = `tabs__tab${isFirst ? ' tabs__tab--active' : ''}`;
      button.setAttribute('tabindex', isFirst ? '0' : '-1');
      button.textContent = tabName;

      // Analytics attributes for the GTM click listener.
      // Use the stable, handleized panel id as the identifier — never the
      // visible (merchant-entered) tab name, which may be long or unrestricted.
      const stableTabId = panel.id.replace('panel-', '');
      button.setAttribute('data-analytics', 'element_click');
      button.setAttribute('data-analytics-name', 'tab_button');
      button.setAttribute('data-component', 'tab');
      button.setAttribute('data-component-type', 'tab_control');
      button.setAttribute('data-component-position', String(index + 1));
      button.setAttribute('data-cta', 'select_tab');
      button.setAttribute('data-tab-id', stableTabId);
      
      if (tabName != ''){
        this.tabNav.appendChild(button);
      }
      
      // Ensure panel has active class if first
      if (isFirst) {
        panel.classList.add('tabs__panel--active');
      }
    });
  }
  
  activateTab(tabIndex) {
    this.tabs.forEach((tab, index) => {
      const isActive = index === tabIndex;
      tab.setAttribute('aria-selected', isActive);
      tab.classList.toggle('tabs__tab--active', isActive);
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    
    this.panels.forEach((panel, index) => {
      const isActive = index === tabIndex;
      panel.classList.toggle('tabs__panel--active', isActive);
    });
  }
  
  attachEventListeners() {
    this.tabs.forEach((tab, index) => {
      // Click handler
      tab.addEventListener('click', () => {
        this.activateTab(index);
      });
      
      // Keyboard navigation
      tab.addEventListener('keydown', (e) => {
        let newIndex = index;
        
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          newIndex = (index + 1) % this.tabs.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          newIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        } else if (e.key === 'Home') {
          e.preventDefault();
          newIndex = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          newIndex = this.tabs.length - 1;
        } else {
          return;
        }
        
        this.activateTab(newIndex);
        this.tabs[newIndex].focus();
      });
    });
  }
  
  activateFromHash() {
    const hash = window.location.hash.toLowerCase().trim();
    if (!hash) return;

    const targetHash = hash.substring(1);

    let targetIndex = -1;
    this.panels.forEach((panel, index) => {
      const tabHandle = panel.dataset.tabName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const panelId = panel.id.toLowerCase();

      if (tabHandle === targetHash || panelId === `panel-${targetHash}` || panelId === targetHash) {
        targetIndex = index;
      }
    });

    if (targetIndex !== -1) {
      setTimeout(() => {
        this.activateTab(targetIndex);
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }
}

// Initialize all tabs on the page
function initializeTabs() {
  const tabContainers = document.querySelectorAll('.tabs');
  tabContainers.forEach(container => {
    if (!container.dataset.tabsInitialized) {
      new MBTabs(container);
      container.dataset.tabsInitialized = 'true';
    }
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeTabs);
} else {
  initializeTabs();
}

// Also run when sections are loaded/reloaded in the theme editor
document.addEventListener('shopify:section:load', initializeTabs);
document.addEventListener('shopify:section:reorder', initializeTabs);
document.addEventListener('mb:quickview:loaded', initializeTabs);
document.addEventListener('mb:purchaseoption:sectionrefresh', initializeTabs);

document.addEventListener('click', function(e) {
  var btn = e.target.closest('.purchase-option.toggle[data-target], .toggle[data-target], [data-target]');
  
  // Make sure we only process clicks for our specific toggle buttons (not other things with data-target)
  if (!btn || !btn.hasAttribute('data-target') || btn.closest('.tabs')) return;
  
  var section = btn.closest('[id^="shopify-section-"]') || document;
  var buttons = Array.from(section.querySelectorAll('[data-target]:not(.tabs [data-target])'));
  var options = Array.from(section.querySelectorAll('[data-option]'));
  var btnIndex = buttons.indexOf(btn);
  if (btnIndex === -1) return;
      var targetIndex = btn.getAttribute('data-target');

      // If first button is clicked while second button is active, show help link
      if (
        btnIndex === 0 &&
        buttons[1] &&
        buttons[1].getAttribute('data-active') === 'true'
      ) {
        var helpLink = document.querySelector('[data-name="need-help-link"]');

        if (helpLink) {
          helpLink.style.display = 'block';
        }
      }

      // Toggle purchase options
      options.forEach((opt, i) => {
        const input = opt.querySelector('.purchase-option-input');
        
        if (i == targetIndex) {
          opt.setAttribute('data-active', 'true');
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          opt.removeAttribute('data-active');
          if (input) {
            input.checked = false;
          }
        }
      });

      // Toggle buttons
      buttons.forEach(b => {
    if (b === btn) {
      b.setAttribute('data-active', 'true');
    } else {
      b.removeAttribute('data-active');
    }
  });

      // Toggle billboard purchase options
      document.querySelectorAll('.billboard [data-option]').forEach((opt, i) => {
        const optVal = opt.getAttribute('data-option');
        if (optVal !== null ? (optVal === targetIndex) : (i == targetIndex)) {
          opt.setAttribute('data-active', 'true');
        } else {
          opt.removeAttribute('data-active');
        }
      });
});

function initStickyFooter(stickyFooter) {

  if (!stickyFooter) return;
  if (stickyFooter.dataset.stickyFooterInitialized) return;

  stickyFooter.dataset.stickyFooterInitialized = 'true';

  // Create sentinel at original DOM position
  const sentinel = document.createElement('div');
  stickyFooter.parentNode.insertBefore(sentinel, stickyFooter);

  function toggleStickyFooter() {
    const purchaseOption = stickyFooter.closest('purchase-option');
    const purchaseOptionInput = purchaseOption?.querySelector('.purchase-option-input');
    const isActive = purchaseOptionInput && purchaseOptionInput.checked;

    if(purchaseOptionInput && !isActive) return; // Skip if the purchase option is not active.
    
    if(stickyFooter.parentNode.offsetParent === null) return; // Skip if there is no visible parent.

    const rect = sentinel.getBoundingClientRect();
    // Only show the sticky footer if the user has started scrolling down
    // and the original position is out of view (above viewport or below viewport)
    if (window.scrollY > 100 && (rect.top <= 0)) {  // || rect.bottom >= window.innerHeight NOT ABOVE, ONLY BELOW
      stickyFooter.classList.add('sticky-footer-show');
    } else {
      stickyFooter.classList.remove('sticky-footer-show');
    }
  }

  window.addEventListener('scroll', toggleStickyFooter);
  window.addEventListener('resize', toggleStickyFooter);

  // Initial check
  toggleStickyFooter();

}

function initializeStickyFooter() {
  document.querySelectorAll('.sticky-footer').forEach(stickyFooter => {
    initStickyFooter(stickyFooter);
  });
}
document.addEventListener('shopify:section:load', event => {
  initializeStickyFooter(event.target);
});
document.addEventListener('mb:purchaseoption:sectionrefresh', initializeStickyFooter);

// Run on DOM ready
if (document.readyState === 'loading') {
document.addEventListener('DOMContentLoaded', initializeStickyFooter);
} else {
  initializeStickyFooter();

}

// scroll into a SAS
function scrollToSas(buttonElement) {
  let targetElement;
  
  if (buttonElement.getAttribute('data-action') === 'scroll-to-sas') {
    const isVisible = (el) => el.offsetWidth > 0 || el.offsetHeight > 0;
    const targets = Array.from(document.querySelectorAll('.purchase-option.toggle, .purchase-option-toggle')).filter(isVisible);

    if (targets.length > 0) {
      const buttonTop = buttonElement.getBoundingClientRect().top;
      const closestToggle = targets.reduce((closest, el) => {
        const dist = Math.abs(el.getBoundingClientRect().top - buttonTop);
        return dist < closest.dist ? { el, dist } : closest;
      }, { el: targets[0], dist: Infinity }).el;

      targetElement = closestToggle.closest('.shopify-section.section-wrapper, .shopify-section, .section-wrapper') || closestToggle;
    }
  } else {
    const targetSelector = buttonElement.getAttribute('data-target');
    if (targetSelector) {
      targetElement = document.querySelector(targetSelector);
    }
  }
  
  if (targetElement) {
    targetElement.style.scrollMarginTop = '0px';
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 550);
  }
}


/* Custom video autoplay and unmute logic based on IntersectionObserver and engagement*/
/* mb_video elements now use additional_attributes="autoplay_onview" instead of the default autoplay attribute so all videos don't auto play at once. */
(function () {
  const VISIBILITY_THRESHOLD = 0.6;

  let activeAudioVideo = null;
  let userAudioEnabled = false; // Tracks if the user has enabled audio on any video

  function getVideos() {
    return Array.from(
      document.querySelectorAll(
        'video[additional_attributes="autoplay_onview"]'
      )
    );
  }

  function muteAllExcept(activeVideo) {
    getVideos().forEach(video => {
      if (video !== activeVideo) {
        video.muted = true;
      }
    });

    activeAudioVideo = activeVideo;
  }

  function activateAudio(video) {
    muteAllExcept(video);

    video.muted = false;
    activeAudioVideo = video;

    video.play().catch(() => {});
  }

  function muteVideo(video) {
    video.muted = true;

    if (activeAudioVideo === video) {
      activeAudioVideo = null;
    }
  }

  function playVideo(video) {
    video.playsInline = true;

    if (userAudioEnabled) {
      muteAllExcept(video);
      video.muted = false;
      activeAudioVideo = video;
    } else {
      video.muted = true;
    }

    video.play().catch(error => {
      console.debug(
        'Video autoplay failed:',
        error
      );
    });
  }

  function pauseVideo(video) {
    video.pause();

    if (video === activeAudioVideo) {
      muteVideo(video);
    }
  }

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const video = entry.target;

        if (
          entry.intersectionRatio >=
          VISIBILITY_THRESHOLD
        ) {
          if (video.preload === 'none') {
            video.preload = 'auto';
          }

          playVideo(video);
        } else {
          pauseVideo(video);
        }
      });
    },
    {
      threshold: [0, VISIBILITY_THRESHOLD]
    }
  );

  function initializeVideo(video) {
    // Prevent double initialization
    if (video.dataset.autoplayOnviewInitialized) {
      return;
    }

    video.dataset.autoplayOnviewInitialized = 'true';

    video.removeAttribute('autoplay');

    video.preload = 'none';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    /*
     * Poster flash workaround
     */
    const poster = video.getAttribute('poster');
    const parent = video.parentElement;

    if (poster && parent) {
      if (
        getComputedStyle(parent).position === 'static'
      ) {
        parent.style.position = 'relative';
      }

      parent.style.backgroundImage =
        `url("${poster}")`;

      parent.style.backgroundSize = 'cover';
      parent.style.backgroundPosition = 'center';
      parent.style.backgroundRepeat = 'no-repeat';

      video.style.opacity = '0';

      const reveal = () => {
        video.style.opacity = '1';
      };

      video.addEventListener(
        'loadeddata',
        reveal,
        { once: true }
      );

      video.addEventListener(
        'playing',
        reveal
      );
    }

    /*
     * Native audio control safety.
     */
    video.addEventListener(
      'volumechange',
      () => {
        userAudioEnabled = !video.muted && video.volume > 0;
        if (
          !video.muted &&
          video.volume > 0
        ) {
          if (video !== activeAudioVideo) {
            muteAllExcept(video);
          }
        } else if (
          video === activeAudioVideo
        ) {
          activeAudioVideo = null;
        }
      }
    );

    observer.observe(video);
  }

  function initializeVideos() {
    getVideos().forEach(initializeVideo);
  }

  /*
   * Run immediately.
   */
  initializeVideos();

  /*
   * Run again when DOM parsing completes.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeVideos,
      { once: true }
    );
  }

  /*
   * Run again once everything has loaded.
   *
   * This is intentionally a fallback, not
   * the primary trigger.
   */
  window.addEventListener(
    'load',
    initializeVideos,
    { once: true }
  );

  /*
   * Catch Shopify/carousel DOM changes.
   */
  const mutationObserver =
    new MutationObserver(() => {
      initializeVideos();
    });

  mutationObserver.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

})();
